/**
 * The reading screen's breadcrumb: Homework > course · exercise · pages. Every part is optional but the page count:
 * one that is unknown, still loading or errored is left out, and the crumbs never show an error or loading state.
 * Course and exercise names are course material, so they are plain text in `<bdi>`.
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { queryKeys } from '../../api/query-keys.js';
import { useApi } from '../../app/api-context.js';
import { courseShortName } from '../../app/course-names.js';
import { useCourseFocus } from '../../app/course-focus.js';
import { useLocale } from '../../i18n/locale.js';
import { useHomeworkLabels } from '../../i18n/homework-labels.js';
import { morphName } from '../../app/page-morph.js';

export function HomeworkCrumbs({
  uploadId,
  pageCount,
}: {
  readonly uploadId: string;
  readonly pageCount: number;
}): React.JSX.Element {
  const t = useHomeworkLabels();
  const api = useApi();
  const { selectedCourses } = useCourseFocus();
  const { locale } = useLocale();
  const list = useQuery({ queryKey: queryKeys.homeworkList, queryFn: () => api.listHomework() });
  const exercises = useQuery({ queryKey: queryKeys.homeworkExercises, queryFn: () => api.homeworkExercises() });

  const mine = list.data?.uploads.find((item) => item.uploadId === uploadId);
  const found = selectedCourses.find((c) => c.moodleCourseId === mine?.courseRef);
  const course = found === undefined ? undefined : courseShortName(found, locale);
  const title = (exercises.data?.exercises ?? []).find(
    (item) => item.courseRef === mine?.courseRef && item.exerciseId === mine?.exerciseId,
  )?.title;

  const parts: React.ReactNode[] = [];
  if (course !== undefined && course !== '') parts.push(<bdi key="course">{course}</bdi>);
  if (title !== undefined && title !== '') parts.push(<bdi key="title">{title}</bdi>);
  parts.push(<span key="pages">{t.pagesInUpload(pageCount)}</span>);

  return (
    <nav aria-label={t.crumbsLabel} className="hw-crumbs" style={morphName('upload', uploadId)}>
      <Link to="/homework">
        <span aria-hidden="true">‹ </span>
        {t.homeworkHeading}
      </Link>
      <span className="hw-crumbs-sep" aria-hidden="true">
        ›
      </span>
      <span className="hw-crumbs-here">
        {parts.map((part, i) => (
          <span key={i}>
            {i > 0 ? ' · ' : ''}
            {part}
          </span>
        ))}
      </span>
    </nav>
  );
}
